/**
 * The Bookings tab rows for the new console (Traci's review, 23 Sep 2026): name, location, package, event date,
 * booking date and status (Pending / Post / Completed; completed and cancelled bookings live under Archived), with
 * the lifecycle bar and booking details in the expanded row. Pure.
 */
import { lifecycleView, questionnaireFact, questionnaireState, assignedRoles } from '../booking/lifecycleView.js'
import { addonFamily } from '../addons/addonFamilies.js'
import { videoAddonHours, videoAddonLabel } from '../addons/videoAddonHours.js'
import { normalizePackageType, deliverablePhotos } from '../constants/packagePricing.js'

const TYPE_LABEL = { photo: 'Photo', photoVideo: 'Photo + Video', videoOnly: 'Video', engagement: 'Engagement Session' }
const ROLE_LABEL = { photographer: 'Photographer', second_photographer: 'Second Photographer', videographer: 'Videographer' }
const dateOnly = v => (v ? String(v).slice(0, 10) : '')
const asArray = v => (Array.isArray(v) ? v : v ? [v] : [])

/** "8 hours · Photo + Video · 410 photos". Traci's doc: every booking shows its photo count, following added hours. */
export function packageText(b) {
  const type = normalizePackageType(b?.package_type)
  const hours = Number(b?.hours_booked)
  const parts = []
  if (Number.isFinite(hours) && hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`)
  parts.push(TYPE_LABEL[type] || (b?.package_type ? String(b.package_type) : 'Booking'))
  const photos = deliverablePhotos(b)
  if (photos) parts.push(`${photos} photos`)
  return parts.join(' · ')
}

/** Add-on names from every place a purchase is recorded, one per family. */
export function addonNames(b) {
  const out = new Map()
  const add = (src, name) => {
    const fam = addonFamily(src) || String(typeof src === 'string' ? src : src?.code || name || '').toLowerCase()
    if (!fam || out.has(fam)) return
    out.set(fam, name || (typeof src === 'string' ? src : src?.name || src?.code) || fam)
  }
  for (const r of asArray(b?.booking_addons)) { const a = r?.addon || r?.addons; if (a) add(a, a.name) }
  for (const a of asArray(b?.personalization_data?.addons)) add(a, typeof a === 'object' ? a.name || a.label : null)
  for (const a of asArray(b?.add_ons)) add(a, typeof a === 'object' ? a.name : null)
  // Nautica, 28 Sep 2026: the Video Add-On shows its hours
  if (out.has('video_addon')) out.set('video_addon', videoAddonLabel(videoAddonHours(b)))
  return [...out.entries()].map(([fam, n]) => (fam === 'video_addon' ? n : String(n).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())))
}

export function talentList(b, names = new Map()) {
  return assignedRoles(b).map(r => ({ role: r.role, role_label: ROLE_LABEL[r.role], talent_id: r.id, name: names.get(r.id) || null }))
}

/**
 * Rose Intakes (the_rose_photo waitlist and $199/$1,000-down intakes), folded in from the current console's Bookings
 * tab (Trera, 26 Sep 2026). Same rule as that page: they sit on their own tab, never among Active or Archived.
 */
export function isRoseBooking(b) {
  const pd = b?.personalization_data || {}
  return b?.source === 'rose' || pd.brand === 'the_rose_photo' || pd.rose_booking === true || pd.rose_waitlist === true
}

/** What the Rose tab shows. `payment` and `amount_paid_cents` are money: the server removes them for non-Billing roles. */
export function roseDetails(b) {
  if (!isRoseBooking(b)) return null
  const pd = b.personalization_data || {}
  const r = pd.rose || {}
  const waitlist = pd.rose_waitlist === true
  const cents = Number(r.amountPaidCents)
  return {
    type: waitlist ? 'Rose Waitlist' : 'Rose Intake',
    name: r.customerName || null,
    partner: r.partnerName || null,
    email: r.email || null,
    phone: r.phone || r.phoneNumber || r.customerPhone || null,
    registry: r.registryNumber || null,
    requested_date: dateOnly(r.weddingDate) || null,
    location: r.eventLocation || r.destination || r.weddingCityCountry || b.venue_address?.formatted || [b.location_city, b.location_state].filter(Boolean).join(', ') || null,
    payment: waitlist ? 'Waitlist submission - no payment' : r.paymentLabel || (b.payment_plan === 'rose_199_down_processing' ? '$199 down + $195 processing fee' : '$1,000 down'),
    amount_paid_cents: Number.isFinite(cents) ? cents : Math.round(Number(b.amount_paid || b.deposit_amount || 0) * 100)
  }
}

/** Rose rows without the money fields (§5.1: amounts are Admin + Billing only). */
export function withoutRoseMoney(rows) {
  return rows.map(r => (r.rose ? { ...r, rose: { ...r.rose, payment: undefined, amount_paid_cents: undefined } } : r))
}

export function toListRow(b, { names = new Map(), questionnaires = new Map(), history = new Map(), today }) {
  const qStatus = questionnaires.has(b.id) ? questionnaires.get(b.id) : null
  const view = lifecycleView(b, { history: history.get(b.id) || [], questionnaire: questionnaireFact({ status: qStatus, eventDate: b.event_date, today }) }, today)
  return {
    id: b.id,
    client_id: b.customer_id || null,
    client_name: names.get(b.customer_id) || null,
    location: [b.location_city, b.location_state].filter(Boolean).join(', ') || null,
    package: packageText(b),
    event_date: dateOnly(b.event_date) || null,
    booking_date: dateOnly(b.created_at) || null,
    status: view.status,
    archived: view.archived,
    current: view.current,
    current_label: view.current_label,
    stages: view.stages.map(s => ({ key: s.key, label: s.label, state: s.state, done: s.done, total: s.total })),
    addons: addonNames(b),
    talent: talentList(b, names),
    is_test: Boolean(b.is_test),
    questionnaire: questionnaireState(qStatus),
    rose: roseDetails(b)
  }
}

/**
 * Cards / Lifecycle view (prototype v108 §3.1): one column per lifecycle stage that has bookings, in lifecycle order,
 * headed by the stage and its count. A booking sits in the column of its current stage (finished ones under Booking
 * Complete). Rows keep the order they come in (the page's sort).
 */
export function groupByStage(rows) {
  const order = new Map()
  const cols = new Map()
  for (const r of rows || []) {
    for (const [i, s] of (r.stages || []).entries()) if (!order.has(s.key)) order.set(s.key, i)
    const key = r.current || 'booking_complete'
    if (!cols.has(key)) cols.set(key, { key, label: r.current ? r.current_label : 'Booking Complete', rows: [] })
    cols.get(key).rows.push(r)
  }
  return [...cols.values()]
    .map(c => ({ ...c, count: c.rows.length }))
    .sort((a, b) => (order.has(a.key) ? order.get(a.key) : 99) - (order.has(b.key) ? order.get(b.key) : 99))
}

/**
 * Nautica's client search (Traci, Oct 2026, the same idea as Kendra's search on Billing): the client's active bookings
 * by client name or booking ID, earliest event first. She picks the booking when a client has more than one. Rose
 * intakes, tests and archived bookings never match. Needs 2 characters.
 */
export function clientBookingMatches(rows, q) {
  const needle = String(q || '').trim().toLowerCase()
  if (needle.length < 2) return []
  return filterRows(rows, { tab: 'active' }).filter(r => [r.client_name, r.id].some(v => String(v || '').toLowerCase().includes(needle)))
}

/**
 * Filter + sort for the tab: Active or Archived (Rose intakes on neither), or Rose (newest submission first); free
 * text over client, id, location, package, talent and the Rose partner, email, location and registry number.
 */
export function filterRows(rows, { tab = 'active', q = '', sort = 'event', questionnaire = '' } = {}) {
  const needle = String(q || '').trim().toLowerCase()
  const inTab = r => (tab === 'rose' ? Boolean(r.rose) : !r.rose && (tab === 'archived' ? r.archived : !r.archived))
  // Laura, 29 Sep 2026: questionnaire reminders without opening every booking. 'not_submitted' = not started or started.
  const qOk = r => !questionnaire || (questionnaire === 'not_submitted' ? r.questionnaire !== 'submitted' : r.questionnaire === questionnaire)
  const out = rows.filter(r => inTab(r) && !r.is_test && qOk(r))
    .filter(r => !needle || [r.client_name, r.id, r.location, r.package, ...r.talent.map(t => t.name), r.rose?.name, r.rose?.partner, r.rose?.email, r.rose?.location, r.rose?.registry].filter(Boolean).join(' ').toLowerCase().includes(needle))
  if (tab === 'rose') return out.sort((a, b) => String(b.booking_date || '').localeCompare(String(a.booking_date || '')))
  // Traci's doc: Booking Date newest first; Event Date earliest first, straight down to the latest (past events
  // included, so the oldest open event starts the list). Archived reads newest first either way.
  const at = (r, k) => String(r[k] || '').slice(0, 10)
  const desc = k => (a, b) => (at(b, k) || '0000').localeCompare(at(a, k) || '0000')
  if (sort === 'booking' || tab === 'archived') return out.sort(desc(sort === 'booking' ? 'booking_date' : 'event_date'))
  return out.sort((a, b) => (at(a, 'event_date') || '9999').localeCompare(at(b, 'event_date') || '9999'))
}
